import { supabase } from '../lib/supabase';

export const emailNotificationService = {
  // ============================================================================
  // EMAIL SENDING
  // ============================================================================

  async sendComplianceDeadlineAlert(recipients, deadline) {
    try {
      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background: #dc2626; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background: #f9fafb; }
            .deadline { background: #fef2f2; border-left: 4px solid #dc2626; padding: 15px; margin: 20px 0; }
            .footer { text-align: center; padding: 20px; color: #6b7280; font-size: 12px; }
            .button { display: inline-block; padding: 12px 24px; background: #3b82f6; color: white; text-decoration: none; border-radius: 6px; margin: 10px 0; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>⚠️ Compliance Deadline Alert</h1>
            </div>
            <div class="content">
              <p>Dear Stakeholder,</p>
              <p>This is an automated alert regarding an upcoming compliance deadline that requires your attention.</p>
              
              <div class="deadline">
                <h3>${deadline?.title || 'Compliance Deadline'}</h3>
                <p><strong>Type:</strong> ${deadline?.type || 'N/A'}</p>
                <p><strong>Due Date:</strong> ${deadline?.dueDate ? new Date(deadline.dueDate)?.toLocaleDateString('en-PG', { day: '2-digit', month: 'long', year: 'numeric' }) : 'N/A'}</p>
                <p><strong>Severity:</strong> ${deadline?.severity || 'High'}</p>
                ${deadline?.affectedCount ? `<p><strong>Affected Employees:</strong> ${deadline?.affectedCount}</p>` : ''}
              </div>

              <p><strong>Action Required:</strong></p>
              <p>${deadline?.message || 'Please review and take necessary action before the deadline.'}</p>

              <a href="${window.location?.origin}/compliance-overview" class="button">View Compliance Dashboard</a>
            </div>
            <div class="footer">
              <p>This is an automated message from PNG HR Management System</p>
              <p>Pacific RBS Ltd | admin@hrpacificrbs.com | +675 78808282</p>
            </div>
          </div>
        </body>
        </html>
      `;

      const { data, error } = await supabase?.functions?.invoke('send-compliance-email', {
        body: {
          to: recipients,
          subject: `⚠️ Compliance Deadline Alert: ${deadline?.title || 'Action Required'}`,
          html: html,
          type: 'compliance_deadline',
          metadata: {
            deadlineId: deadline?.id,
            dueDate: deadline?.dueDate,
            severity: deadline?.severity
          }
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending compliance deadline alert:', error);
      throw error;
    }
  },

  async sendViolationReport(recipients, violations, reportPeriod) {
    try {
      const violationRows = violations?.map(v => `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${v?.violationType?.replace(/_/g, ' ')}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${v?.severity}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${v?.department || 'N/A'}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${v?.affectedCount || 0}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">K${(v?.estimatedCost || 0)?.toFixed(2)}</td>
        </tr>
      `)?.join('');

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 800px; margin: 0 auto; padding: 20px; }
            .header { background: #dc2626; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background: #f9fafb; }
            .summary { background: white; padding: 15px; margin: 20px 0; border-radius: 8px; }
            .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 15px 0; }
            .summary-card { text-align: center; padding: 15px; background: #f3f4f6; border-radius: 6px; }
            .summary-card h3 { margin: 0; font-size: 24px; color: #dc2626; }
            .summary-card p { margin: 5px 0 0 0; color: #6b7280; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; background: white; }
            th { background: #f3f4f6; padding: 12px; text-align: left; font-weight: 600; }
            .footer { text-align: center; padding: 20px; color: #6b7280; font-size: 12px; }
            .button { display: inline-block; padding: 12px 24px; background: #3b82f6; color: white; text-decoration: none; border-radius: 6px; margin: 10px 0; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>📊 Compliance Violation Report</h1>
              <p>${reportPeriod || 'Current Period'}</p>
            </div>
            <div class="content">
              <p>Dear Stakeholder,</p>
              <p>Please find below the compliance violation report for your review.</p>
              
              <div class="summary">
                <h3>Executive Summary</h3>
                <div class="summary-grid">
                  <div class="summary-card">
                    <h3>${violations?.length || 0}</h3>
                    <p>Total Violations</p>
                  </div>
                  <div class="summary-card">
                    <h3>${violations?.filter(v => v?.severity === 'critical')?.length || 0}</h3>
                    <p>Critical Violations</p>
                  </div>
                  <div class="summary-card">
                    <h3>K${violations?.reduce((sum, v) => sum + (v?.estimatedCost || 0), 0)?.toFixed(2)}</h3>
                    <p>Total Estimated Cost</p>
                  </div>
                </div>
              </div>

              <h3>Violation Details</h3>
              <table>
                <thead>
                  <tr>
                    <th>Violation Type</th>
                    <th>Severity</th>
                    <th>Department</th>
                    <th>Affected</th>
                    <th>Est. Cost</th>
                  </tr>
                </thead>
                <tbody>
                  ${violationRows}
                </tbody>
              </table>

              <p><strong>Recommended Actions:</strong></p>
              <ul>
                <li>Review all critical violations immediately</li>
                <li>Implement corrective actions for high-priority items</li>
                <li>Schedule remediation for remaining violations</li>
                <li>Monitor compliance metrics regularly</li>
              </ul>

              <a href="${window.location?.origin}/compliance-library" class="button">View Full Report</a>
            </div>
            <div class="footer">
              <p>This is an automated message from PNG HR Management System</p>
              <p>Pacific RBS Ltd | admin@hrpacificrbs.com | +675 78808282</p>
            </div>
          </div>
        </body>
        </html>
      `;

      const { data, error } = await supabase?.functions?.invoke('send-compliance-email', {
        body: {
          to: recipients,
          subject: `📊 Compliance Violation Report - ${reportPeriod || 'Current Period'}`,
          html: html,
          type: 'violation_report',
          metadata: {
            reportPeriod: reportPeriod,
            violationCount: violations?.length,
            criticalCount: violations?.filter(v => v?.severity === 'critical')?.length
          }
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending violation report:', error);
      throw error;
    }
  },

  // ============================================================================
  // STAKEHOLDER MANAGEMENT
  // ============================================================================

  async getStakeholdersByRole(role) {
    try {
      const { data, error } = await supabase
        ?.from('user_role_assignments')
        ?.select(`
          user_email,
          custom_roles (
            role_name
          )
        `)
        ?.eq('custom_roles.role_name', role)
        ?.eq('is_active', true);

      if (error) throw error;
      return data?.map(row => row?.user_email) || [];
    } catch (error) {
      console.error('Error fetching stakeholders by role:', error);
      return [];
    }
  },

  async getStakeholdersByDepartment(department) {
    try {
      const { data, error } = await supabase
        ?.from('employees')
        ?.select('email')
        ?.eq('department', department)
        ?.eq('is_active', true);

      if (error) throw error;
      return data?.map(row => row?.email) || [];
    } catch (error) {
      console.error('Error fetching stakeholders by department:', error);
      return [];
    }
  },

  async getScheduledDeliveryRecipients(scheduledDeliveryId) {
    try {
      const { data, error } = await supabase
        ?.from('scheduled_report_deliveries')
        ?.select('recipient_emails, recipient_departments')
        ?.eq('id', scheduledDeliveryId)
        ?.single();

      if (error) throw error;

      let allRecipients = [...(data?.recipient_emails || [])];

      // Get emails from departments
      if (data?.recipient_departments?.length > 0) {
        for (const dept of data?.recipient_departments) {
          const deptEmails = await this.getStakeholdersByDepartment(dept);
          allRecipients = [...allRecipients, ...deptEmails];
        }
      }

      // Remove duplicates
      return [...new Set(allRecipients)];
    } catch (error) {
      console.error('Error fetching scheduled delivery recipients:', error);
      return [];
    }
  },

  // ============================================================================
  // AUTOMATED TRIGGERS
  // ============================================================================

  async checkAndSendDeadlineAlerts() {
    try {
      // Get upcoming deadlines (within 7 days)
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow?.setDate(sevenDaysFromNow?.getDate() + 7);

      const { data: alerts, error } = await supabase
        ?.from('system_alerts')
        ?.select('*')
        ?.eq('alert_type', 'nasfund_deadline')
        ?.eq('alert_status', 'unread')
        ?.lte('deadline', sevenDaysFromNow?.toISOString());

      if (error) throw error;

      // Get compliance officers and payroll managers
      const complianceOfficers = await this.getStakeholdersByRole('compliance_officer');
      const payrollManagers = await this.getStakeholdersByRole('payroll_manager');
      const recipients = [...new Set([...complianceOfficers, ...payrollManagers])];

      if (recipients?.length === 0) {
        console.warn('No stakeholders found for deadline alerts');
        return;
      }

      // Send alerts
      for (const alert of alerts || []) {
        await this.sendComplianceDeadlineAlert(recipients, {
          id: alert?.id,
          title: alert?.title,
          type: alert?.alert_type,
          dueDate: alert?.deadline,
          severity: alert?.severity,
          affectedCount: alert?.affected_count,
          message: alert?.message
        });
      }

      return { sent: alerts?.length || 0, recipients: recipients?.length };
    } catch (error) {
      console.error('Error checking and sending deadline alerts:', error);
      throw error;
    }
  },

  async sendScheduledViolationReports() {
    try {
      // Get active scheduled deliveries
      const { data: schedules, error } = await supabase
        ?.from('scheduled_report_deliveries')
        ?.select('*')
        ?.eq('is_active', true)
        ?.lte('next_delivery_date', new Date()?.toISOString());

      if (error) throw error;

      for (const schedule of schedules || []) {
        // Get violations
        const { data: violations } = await supabase
          ?.from('compliance_violations')
          ?.select('*')
          ?.eq('violation_status', 'identified')
          ?.order('severity', { ascending: false });

        // Get recipients
        const recipients = await this.getScheduledDeliveryRecipients(schedule?.id);

        if (recipients?.length > 0 && violations?.length > 0) {
          await this.sendViolationReport(
            recipients,
            violations,
            `${schedule?.schedule_name} - ${new Date()?.toLocaleDateString('en-PG')}`
          );

          // Update last delivery date
          await supabase
            ?.from('scheduled_report_deliveries')
            ?.update({
              last_delivery_date: new Date()?.toISOString(),
              next_delivery_date: this.calculateNextDeliveryDate(schedule)
            })
            ?.eq('id', schedule?.id);
        }
      }

      return { processed: schedules?.length || 0 };
    } catch (error) {
      console.error('Error sending scheduled violation reports:', error);
      throw error;
    }
  },

  calculateNextDeliveryDate(schedule) {
    const now = new Date();
    const next = new Date(now);

    switch (schedule?.delivery_frequency) {
      case 'daily':
        next?.setDate(next?.getDate() + 1);
        break;
      case 'weekly':
        next?.setDate(next?.getDate() + 7);
        break;
      case 'monthly':
        next?.setMonth(next?.getMonth() + 1);
        break;
      case 'quarterly':
        next?.setMonth(next?.getMonth() + 3);
        break;
      default:
        next?.setDate(next?.getDate() + 1);
    }

    return next?.toISOString();
  },

  // ============================================================================
  // PAYSLIP NOTIFICATIONS
  // ============================================================================

  async sendPayslipNotification(employeeEmail, payslipData) {
    try {
      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background: #10b981; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background: #f9fafb; }
            .payslip-details { background: white; padding: 20px; margin: 20px 0; border-radius: 8px; border: 1px solid #e5e7eb; }
            .detail-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #f3f4f6; }
            .detail-label { font-weight: 600; color: #6b7280; }
            .detail-value { color: #111827; }
            .total-row { background: #f0fdf4; padding: 15px; margin-top: 15px; border-radius: 6px; }
            .total-row .detail-label { color: #065f46; font-size: 16px; }
            .total-row .detail-value { color: #065f46; font-size: 18px; font-weight: bold; }
            .footer { text-align: center; padding: 20px; color: #6b7280; font-size: 12px; }
            .button { display: inline-block; padding: 12px 24px; background: #3b82f6; color: white; text-decoration: none; border-radius: 6px; margin: 10px 0; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>💰 Your Payslip is Ready</h1>
            </div>
            <div class="content">
              <p>Dear ${payslipData?.employeeName || 'Employee'},</p>
              <p>Your payslip for the period <strong>${payslipData?.payPeriodStart || ''}</strong> to <strong>${payslipData?.payPeriodEnd || ''}</strong> has been processed and is now available.</p>
              
              <div class="payslip-details">
                <h3>Payslip Summary</h3>
                <div class="detail-row">
                  <span class="detail-label">Payslip Number:</span>
                  <span class="detail-value">${payslipData?.payslipNumber || 'N/A'}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Payment Date:</span>
                  <span class="detail-value">${payslipData?.paymentDate ? new Date(payslipData.paymentDate)?.toLocaleDateString('en-PG', { day: '2-digit', month: 'long', year: 'numeric' }) : 'N/A'}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Gross Pay:</span>
                  <span class="detail-value">K${(payslipData?.grossPay || 0)?.toFixed(2)}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Total Deductions:</span>
                  <span class="detail-value">K${(payslipData?.totalDeductions || 0)?.toFixed(2)}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">SWT:</span>
                  <span class="detail-value">K${(payslipData?.swtAmount || 0)?.toFixed(2)}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">NASFUND (Employee):</span>
                  <span class="detail-value">K${(payslipData?.nasfundEmployee || 0)?.toFixed(2)}</span>
                </div>
                <div class="total-row">
                  <div class="detail-row" style="border: none; padding: 0;">
                    <span class="detail-label">Net Pay:</span>
                    <span class="detail-value">K${(payslipData?.netPay || 0)?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <p><strong>Payment Method:</strong> ${payslipData?.paymentMethod || 'Bank Transfer'}</p>
              <p>Your salary will be deposited to your registered bank account on the payment date.</p>

              <a href="${window.location?.origin}/employee-self-service-portal" class="button">View Full Payslip</a>
            </div>
            <div class="footer">
              <p>This is an automated message from PNG HR Management System</p>
              <p>Pacific RBS Ltd | admin@hrpacificrbs.com | +675 78808282</p>
              <p>If you have any questions about your payslip, please contact HR department.</p>
            </div>
          </div>
        </body>
        </html>
      `;

      const { data, error } = await supabase?.functions?.invoke('send-payslip-leave-notification', {
        body: {
          to: employeeEmail,
          subject: `💰 Payslip Ready - ${payslipData?.payPeriodStart || ''} to ${payslipData?.payPeriodEnd || ''}`,
          html: html,
          type: 'payslip_notification',
          metadata: {
            payslipId: payslipData?.id,
            payslipNumber: payslipData?.payslipNumber,
            employeeId: payslipData?.employeeId,
            netPay: payslipData?.netPay
          }
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending payslip notification:', error);
      throw error;
    }
  },

  // ============================================================================
  // LEAVE APPROVAL ALERTS
  // ============================================================================

  async sendLeaveApprovalAlert(employeeEmail, leaveRequestData, approvalStatus) {
    try {
      const isApproved = approvalStatus === 'approved';
      const statusColor = isApproved ? '#10b981' : '#dc2626';
      const statusIcon = isApproved ? '✅' : '❌';
      const statusText = isApproved ? 'Approved' : 'Rejected';

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background: ${statusColor}; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background: #f9fafb; }
            .leave-details { background: white; padding: 20px; margin: 20px 0; border-radius: 8px; border: 1px solid #e5e7eb; }
            .detail-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #f3f4f6; }
            .detail-label { font-weight: 600; color: #6b7280; }
            .detail-value { color: #111827; }
            .status-badge { display: inline-block; padding: 8px 16px; background: ${isApproved ? '#d1fae5' : '#fee2e2'}; color: ${isApproved ? '#065f46' : '#991b1b'}; border-radius: 20px; font-weight: 600; margin: 10px 0; }
            .footer { text-align: center; padding: 20px; color: #6b7280; font-size: 12px; }
            .button { display: inline-block; padding: 12px 24px; background: #3b82f6; color: white; text-decoration: none; border-radius: 6px; margin: 10px 0; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>${statusIcon} Leave Request ${statusText}</h1>
            </div>
            <div class="content">
              <p>Dear ${leaveRequestData?.employeeName || 'Employee'},</p>
              <p>Your leave request has been <strong>${statusText?.toLowerCase()}</strong>.</p>
              
              <div class="status-badge">${statusIcon} ${statusText}</div>

              <div class="leave-details">
                <h3>Leave Request Details</h3>
                <div class="detail-row">
                  <span class="detail-label">Leave Type:</span>
                  <span class="detail-value">${leaveRequestData?.leaveType?.replace(/_/g, ' ')?.toUpperCase() || 'N/A'}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Start Date:</span>
                  <span class="detail-value">${leaveRequestData?.startDate ? new Date(leaveRequestData.startDate)?.toLocaleDateString('en-PG', { day: '2-digit', month: 'long', year: 'numeric' }) : 'N/A'}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">End Date:</span>
                  <span class="detail-value">${leaveRequestData?.endDate ? new Date(leaveRequestData.endDate)?.toLocaleDateString('en-PG', { day: '2-digit', month: 'long', year: 'numeric' }) : 'N/A'}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Days Requested:</span>
                  <span class="detail-value">${leaveRequestData?.daysRequested || 0} days</span>
                </div>
                ${leaveRequestData?.approvedBy ? `
                <div class="detail-row">
                  <span class="detail-label">Approved By:</span>
                  <span class="detail-value">${leaveRequestData?.approvedBy}</span>
                </div>
                ` : ''}
                ${leaveRequestData?.approvedAt ? `
                <div class="detail-row">
                  <span class="detail-label">Approved At:</span>
                  <span class="detail-value">${new Date(leaveRequestData.approvedAt)?.toLocaleDateString('en-PG', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                ` : ''}
              </div>

              ${isApproved ? `
                <p><strong>Next Steps:</strong></p>
                <ul>
                  <li>Your leave has been recorded in the system</li>
                  <li>Please ensure proper handover of responsibilities</li>
                  <li>Contact your supervisor if you have any questions</li>
                </ul>
              ` : `
                <p><strong>Reason for Rejection:</strong></p>
                <p>${leaveRequestData?.rejectionReason || 'Please contact your supervisor for more details.'}</p>
              `}

              <a href="${window.location?.origin}/employee-self-service-portal" class="button">View Leave History</a>
            </div>
            <div class="footer">
              <p>This is an automated message from PNG HR Management System</p>
              <p>Pacific RBS Ltd | admin@hrpacificrbs.com | +675 78808282</p>
              <p>If you have any questions about your leave request, please contact HR department.</p>
            </div>
          </div>
        </body>
        </html>
      `;

      const { data, error } = await supabase?.functions?.invoke('send-payslip-leave-notification', {
        body: {
          to: employeeEmail,
          subject: `${statusIcon} Leave Request ${statusText} - ${leaveRequestData?.leaveType?.replace(/_/g, ' ')?.toUpperCase() || ''}`,
          html: html,
          type: 'leave_approval_alert',
          metadata: {
            leaveRequestId: leaveRequestData?.id,
            employeeId: leaveRequestData?.employeeId,
            approvalStatus: approvalStatus,
            leaveType: leaveRequestData?.leaveType
          }
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending leave approval alert:', error);
      throw error;
    }
  },
};

export default emailNotificationService;